import { ProcessCellForExportParams } from 'ag-grid-community';
import { mapKeys } from 'lodash';
import { useCallback, useMemo } from 'react';

import { useAuthUser } from 'auth/hooks';
import { AgGridServerTable } from 'core/components';
import { AgGridFilterOption, TableLayoutType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { AgGridServerTableProvider } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { removeNullValuesNestedObject } from 'core/utils';
import { ICorporateUser } from 'corporate/types';
import { ExportEntityType, ExportTimeLogButton } from 'jobs/components/JobList';
import { ProjectType } from 'projects/constants';
import { projectService } from 'projects/services';
import { IExternalProjectAccount, IProject } from 'projects/types';

import { AddProjectButton } from './actions';
import { columnDef } from './columns';
import { ProjectListColId } from './enums';

export const ProjectList = () => {
  const { user } = useAuthUser();

  const columnDefs = useMemo(() => columnDef(), []);

  const defaultColDef = useMemo(() => ({ autoHeight: true }), []);

  const initialState = useMemo(() => {
    return {
      filter: {
        filterModel: {
          corporateUsers: {
            filterType: 'text',
            type: AgGridFilterOption.CONTAINS,
            filter: `${user?.firstName} ${user?.lastName}`,
          },
        },
      },
    };
  }, [user?.firstName, user?.lastName]);

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const mapFilters = useCallback((filters) => {
    let mappedFilters = filters;

    mappedFilters = mapKeys(mappedFilters, (_, key) => {
      switch (key) {
        case 'corporateUsers':
          return 'corporateUserName';
        default:
          return key;
      }
    });

    return removeNullValuesNestedObject(mappedFilters);
  }, []);

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) => {
      return projectService.getProjects(
        removeNullValuesNestedObject({
          filters: mapFilters(filters),
          ...rest,
        })
      );
    },
    [mapFilters]
  );

  const actionButtons = useMemo(() => {
    if (isAlternateVersionEnabled) {
      return [];
    }

    return [<AddProjectButton key="addProjectButton" />];
  }, [isAlternateVersionEnabled]);

  const selectionActionButtons = useMemo(() => {
    return [
      <ExportTimeLogButton
        key="exportTimeLogButton"
        entityType={ExportEntityType.PROJECT}
        isExportLogAllowed={(selectedRows: IProject[]) =>
          selectedRows?.[0]?.type === ProjectType.PROJECT
        }
      />,
    ];
  }, []);

  const excelExportParams = useMemo(
    () => ({
      processCellCallback: (params: ProcessCellForExportParams) => {
        const colId = params.column.getColId();

        if (colId === ProjectListColId.CORPORATE_USERS) {
          if (Array.isArray(params.value)) {
            return params.value.map((user: ICorporateUser) => user.name).join(', ');
          }

          return params.value;
        }

        if (colId === ProjectListColId.EXTERNAL_PROJECT_CUSTOMERS) {
          if (!params.value) {
            return '';
          }

          return params.value
            .map(
              (account: IExternalProjectAccount, index: number) => `
                ${index + 1}. ${account.customerName}}\n
                ${account.accountNumber}
              `
            )
            .join('\n');
        }

        return params.value;
      },
    }),
    []
  );

  return (
    <AgGridServerTableProvider
      key={user?.id}
      tableId={'projectList'}
      entityType="Project"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      initialState={initialState}
      getRows={getRows}
      layoutType={TableLayoutType.PROJECT}
      hasLayoutActions
      hasManageFilters
      excelExportParams={excelExportParams}
    >
      <AgGridServerTable
        actionButtons={actionButtons}
        selectionActionButtons={selectionActionButtons}
      />
    </AgGridServerTableProvider>
  );
};
